import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Clock, Users, Calendar, FileText } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Reveal, RevealLine } from "../lib/motion";
import { LeadModal } from "../components/LeadModal";
import { BookingModal } from "../components/BookingModal";
import { getProgram } from "../academyData";
import { API, startCheckout } from "../lib/api";
import { track } from "../lib/track";
import { PayPalButton } from "../components/PayPalButton";

const eurFmt = (c) => "€" + (c / 100).toLocaleString("it-IT", { maximumFractionDigits: 0 });

export default function AcademyProgram() {
  const { slug } = useParams();
  const program = getProgram(slug);
  const [apply, setApply] = useState(false);
  const [call, setCall] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => {
    fetch(`${API}/academy/config`).then((r) => r.json())
      .then((d) => setCfg(d.courses?.[slug] || null)).catch(() => {});
  }, [slug]);

  if (!program) return <Navigate to="/#academy" replace />;
  const p = program;

  const mode = cfg?.cta_mode || "application";
  const comingSoon = mode === "coming_soon" || cfg?.status === "coming_soon" || cfg?.status === "closed";
  const ctaLabel = cfg?.cta_text || (mode === "direct" ? "Acquista ora" : comingSoon ? "Coming soon" : "Candidati alla prossima edizione");
  const topLabel = mode === "direct" ? "Acquista" : comingSoon ? "Coming soon" : "Candidati";

  const onCta = async () => {
    track("academy_cta_click", { product: slug });
    if (comingSoon) return;
    if (mode === "direct" && cfg?.lookup_key) {
      setPaying(true);
      try {
        track("buy_click", { product: slug });
        const { checkout_url } = await startCheckout(cfg.lookup_key);
        window.location.href = checkout_url;
      } catch { setPaying(false); }
      return;
    }
    setApply(true);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="academy-program-page">
      {/* top bar */}
      <div className="border-b border-[var(--line)] sticky top-0 z-40 bg-white/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-5 flex items-center justify-between">
          <Link to="/#academy" data-testid="program-back" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--petrol)] link-underline">
            <ArrowLeft size={16} strokeWidth={1.5} /> Academy
          </Link>
          <button data-testid="program-apply-top" onClick={onCta} disabled={comingSoon}
            className="btn-fill text-xs font-semibold uppercase tracking-[0.15em] bg-[var(--petrol)] text-white px-6 py-3 disabled:opacity-50">
            {topLabel}
          </button>
        </div>
      </div>

      {/* hero */}
      <header className="max-w-6xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="overline">Executive Program</span>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[var(--accent)] border border-[var(--gold)]/50 px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] animate-pulse" /> {p.cohortStatus}
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl leading-[1.02] text-[var(--ink)] max-w-4xl">{p.title}</h1>
          <p className="mt-5 font-serif text-xl sm:text-2xl italic text-[var(--petrol)] max-w-3xl">{p.tagline}</p>
          <p className="mt-8 text-lg text-[var(--ink-soft)] leading-relaxed max-w-3xl">
            <span className="font-serif text-[var(--ink)] italic">{p.hook} </span>{p.description}
          </p>

          <div className="mt-10 flex flex-wrap gap-8 text-sm">
            <Meta icon={Clock} label={`${p.weeks} · ${p.sessions}`} />
            <Meta icon={Calendar} label="Live online · mar/gio 18:00–19:30 CET" />
            <Meta icon={Users} label={`Edizione ${p.cohortSize}`} />
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="program-apply" onClick={onCta} disabled={comingSoon || paying}
              className="btn-fill text-xs font-semibold uppercase tracking-[0.15em] bg-[var(--petrol)] text-white px-8 py-4 disabled:opacity-50">
              {paying ? "Reindirizzamento…" : ctaLabel}
            </button>
            <button data-testid="program-call" onClick={() => setCall(true)}
              className="btn-fill text-xs font-semibold uppercase tracking-[0.15em] border border-[var(--anthracite)] text-[var(--anthracite)] hover:text-white px-8 py-4">
              Prenota una Executive Call
            </button>
          </div>
        </motion.div>
      </header>

      {/* Executive Lab Method */}
      <section className="bg-[var(--anthracite)] text-white py-16">
        <div className="max-w-6xl mx-auto px-6 sm:px-12">
          <div className="overline !text-[var(--gold)] mb-4">The Executive Lab Method™</div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border-l border-[var(--gold)]/40 pl-6">
              <h3 className="font-serif text-2xl mb-2">Sessione A · Insight & Framework</h3>
              <p className="text-white/70 text-sm leading-relaxed">Il quadro concettuale e i modelli di riferimento, spiegati con taglio executive e prospettiva Board.</p>
            </div>
            <div className="border-l border-[var(--gold)]/40 pl-6">
              <h3 className="font-serif text-2xl mb-2">Sessione B · Executive Lab</h3>
              <p className="text-white/70 text-sm leading-relaxed">Applicazione immediata su casi reali: canvas, template e simulazioni che trasformano il metodo in decisioni.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 sm:px-12 py-16 sm:py-24 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-8 space-y-20">
          {/* audience */}
          <Block title="A chi è rivolto">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {p.audience.map((a, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--ink-soft)]">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />{a}
                </li>
              ))}
            </ul>
          </Block>

          {/* outcomes */}
          <Block title="Cosa porterai a casa">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {p.outcomes.map((o, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check size={18} strokeWidth={1.5} className="text-[var(--petrol)] mt-1 shrink-0" />
                  <span className="text-[var(--ink-soft)]">{o}</span>
                </div>
              ))}
            </div>
          </Block>

          {/* curriculum */}
          <Block title="Programma settimana per settimana">
            <Accordion type="single" collapsible className="w-full" defaultValue="w-0">
              {p.curriculum.map((c, i) => (
                <AccordionItem key={c.week} value={`w-${i}`} className="border-[var(--line)]">
                  <AccordionTrigger data-testid={`week-${c.week}`} className="hover:no-underline py-5 text-left">
                    <span className="flex items-baseline gap-4">
                      <span className="font-serif text-2xl text-[var(--accent)]">{c.week}</span>
                      <span className="font-serif text-xl text-[var(--ink)]">Settimana {parseInt(c.week, 10)}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="grid sm:grid-cols-2 gap-6 pl-1">
                      <div>
                        <div className="overline !text-[var(--anthracite)] mb-2">Sessione A · Insight</div>
                        <p className="text-sm text-[var(--ink-soft)] leading-relaxed">{c.insight}</p>
                      </div>
                      <div>
                        <div className="overline !text-[var(--accent)] mb-2">Sessione B · Executive Lab</div>
                        <p className="text-sm text-[var(--ink-soft)] leading-relaxed">{c.lab}</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Block>

          {/* materials */}
          <Block title="Materiali inclusi">
            <div className="grid sm:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
              {p.materials.map((m, i) => (
                <div key={i} className="bg-white p-5 flex items-center gap-3">
                  <FileText size={18} strokeWidth={1.25} className="text-[var(--gold)] shrink-0" />
                  <span className="text-sm text-[var(--ink)]">{m}</span>
                </div>
              ))}
            </div>
          </Block>

          {/* format */}
          <Block title="Formato">
            <ul className="space-y-3">
              {p.format.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--ink-soft)]">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />{f}
                </li>
              ))}
            </ul>
          </Block>

          {/* faq */}
          <Block title="Domande frequenti">
            <Accordion type="single" collapsible className="w-full">
              {p.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-[var(--line)]">
                  <AccordionTrigger data-testid={`faq-${i}`} className="hover:no-underline py-5 text-left font-serif text-lg text-[var(--ink)]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-[var(--ink-soft)] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Block>
        </div>

        {/* sticky pricing sidebar */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 border border-[var(--line)] p-8 bg-[var(--paper-2)]">
            <div className="overline !text-[var(--gold)] mb-3">{cfg?.price_kind === "promo" ? "Offerta lancio" : "Edizione Fondatrice"}</div>
            <div className="flex items-baseline gap-3">
              <span className="font-serif text-5xl text-[var(--petrol)]" data-testid="program-price">{cfg?.price_effective ? eurFmt(cfg.price_effective) : p.priceLaunch}</span>
              {cfg?.price_kind === "promo" && cfg?.price_standard ? (
                <span className="text-lg text-[var(--ink-soft)] line-through">{eurFmt(cfg.price_standard)}</span>
              ) : null}
            </div>
            <div className="mt-2 text-sm text-[var(--ink-soft)]">
              Prezzo standard {cfg?.price_standard ? eurFmt(cfg.price_standard) : p.priceStandard} · per partecipante{cfg?.vat_included === false ? " · IVA esclusa" : ""}
            </div>
            {cfg?.seats_total ? <div className="mt-1 text-sm text-[var(--ink-soft)]">Posti disponibili: {cfg.seats_total}</div> : null}

            <div className="mt-6 space-y-2 text-sm text-[var(--ink-soft)] border-t border-[var(--line)] pt-6">
              <div className="flex items-center gap-2"><Clock size={15} className="text-[var(--petrol)]" /> {p.weeks} · {p.sessions}</div>
              <div className="flex items-center gap-2"><Users size={15} className="text-[var(--petrol)]" /> Edizione {p.cohortSize}</div>
              <div className="flex items-center gap-2"><Check size={15} className="text-[var(--petrol)]" /> Attestato di partecipazione</div>
            </div>

            <button data-testid="program-apply-side" onClick={onCta} disabled={comingSoon || paying}
              className="btn-fill w-full mt-8 inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] bg-[var(--petrol)] text-white px-8 py-4 disabled:opacity-50">
              {paying ? "Reindirizzamento…" : (mode === "direct" ? (cfg?.cta_text || "Acquista ora") : comingSoon ? "Coming soon" : (cfg?.cta_text || "Candidati alla prossima edizione"))}
            </button>
            {mode === "apply_pay" && !comingSoon && (
              <p className="mt-3 text-xs text-center text-[var(--ink-soft)]">Pagamento richiesto solo dopo l'approvazione della candidatura.</p>
            )}
            {mode === "direct" && !comingSoon && cfg?.lookup_key && (
              <div className="mt-3">
                <PayPalButton lookup_key={cfg.lookup_key} testid="program-paypal" />
              </div>
            )}
            <button data-testid="program-call-side" onClick={() => setCall(true)}
              className="w-full mt-3 text-center text-xs font-semibold uppercase tracking-[0.12em] text-[var(--petrol)] hover:text-[var(--accent)] transition-[color] duration-300 py-2">
              Prenota una Executive Call
            </button>

            <p className="mt-6 text-xs text-[var(--ink-soft)] leading-relaxed border-t border-[var(--line)] pt-6">{p.corporate}</p>
          </div>
        </aside>
      </div>

      <LeadModal open={apply} onClose={() => setApply(false)}
        title="Candidatura all'edizione" subtitle="Candidati alla prossima edizione"
        topic={`Candidatura — ${p.title}`}
        onSuccess={() => track("academy_application", { product: slug })} />
      <BookingModal open={call} onClose={() => setCall(false)}
        topic={`Executive Call — interesse in ${p.title}`} />
    </div>
  );
}

const Meta = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-2 text-[var(--ink-soft)]"><Icon size={16} strokeWidth={1.5} className="text-[var(--petrol)]" /> {label}</span>
);

const Block = ({ title, children }) => (
  <Reveal>
    <h2 className="font-serif text-3xl text-[var(--ink)] mb-8">
      <RevealLine>{title}</RevealLine>
    </h2>
    {children}
  </Reveal>
);
